<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS } from '../constants';
import { messageStamp } from 'shared/helpers/timeHelper';

const { contentAttributes, variant, createdAt } = useMessageContext();

const readableTime = computed(() =>
  messageStamp(createdAt.value, 'MMM d yyyy, h:mm a')
);

// Map message variant to background color, consistent with Base.vue
const variantBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]: 'bg-n-solid-amber text-n-amber-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
};

const bubbleClass = computed(() => {
  return variantBaseMap[variant.value] || 'bg-n-solid-iris text-n-slate-12';
});

// Normalize content attributes to handle both camelCase and snake_case keys,
// since the deep camelCase conversion in MessageList can fail with RangeError
const normalizedAttributes = computed(() => {
  const attrs = contentAttributes?.value || contentAttributes || {};
  return {
    heading: attrs.heading || '',
    subtext: attrs.subtext || '',
    items: (attrs.items || []).map(item => ({
      ...item,
      // Ensure image_url is accessible regardless of key format
      resolvedImageUrl: item?.image_url || item?.imageUrl || '',
      // Ensure action is accessible regardless of key format
      resolvedAction: item?.action || {},
    })),
  };
});

const handleImageError = event => {
  event.target.src =
    'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2248%22 height=%2248%22%3E%3Crect fill=%22%23f0f0f0%22 width=%2248%22 height=%2248%22/%3E%3Ctext fill=%22%23999%22 x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.3em%22%3ENo Image%3C/text%3E%3C/svg%3E';
};

const handleProductClick = item => {
  const action = item?.resolvedAction || item?.action;
  if (action?.type === 'open_url' && action?.value) {
    window.open(action.value, '_blank');
  }
};
</script>

<template>
  <div
    class="text-sm right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm w-full max-w-full sm:max-w-lg overflow-hidden px-3 sm:px-3 py-3"
    :class="bubbleClass"
  >
    <!-- Header -->
    <div v-if="normalizedAttributes.heading || normalizedAttributes.subtext">
      <p
        v-if="normalizedAttributes.subtext"
        class="text-[16px] leading-[20px] font-normal text-[#475569] dark:text-white break-words"
      >
        {{ normalizedAttributes.subtext }}
      </p>
    </div>

    <!-- Items -->
    <div class="grid grid-cols-2 gap-3 py-1">
      <div
        v-for="(item, index) in normalizedAttributes.items"
        :key="item.id || index"
        class="p-3 flex gap-3 cursor-pointer hover:bg-n-alpha-1 transition-colors border border-n-strong rounded-lg"
        @click="handleProductClick(item)"
      >
        <div class="flex-shrink-0">
          <img
            :src="item.resolvedImageUrl"
            :alt="item.title"
            class="w-16 h-16 rounded-lg object-contain"
            loading="lazy"
            @error="handleImageError"
          />
        </div>

        <div class="flex-1 min-w-0">
          <div
            class="font-medium text-[14px] capitalize leading-[100%] tracking-[0%] text-[#475569] dark:text-white break-words line-clamp-2"
            :title="item.title"
          >
            {{ item.title }}
          </div>

          <div
            v-if="item.price"
            class="font-bold text-[14px] leading-[20px] tracking-[0%] text-[#334155] dark:text-white mt-1"
          >
            {{ item.price }}
          </div>

          <div
            v-if="item.meta"
            class="font-normal text-[14px] leading-[20px] tracking-[0%] text-[#64748B] dark:text-white mt-1 break-words"
          >
            {{ item.meta }}
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-end justify-between gap-2 mt-2">
      <span class="text-[10px] leading-[16px] font-normal text-n-slate-11">
        {{ readableTime }}
      </span>
    </div>
  </div>
</template>

<style scoped>
/* intentionally empty */
</style>
